import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { Accordion, AccordionSummary, AccordionDetails, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

interface BankInformationPanelProps {
  missingDocument: WintSuperkollAPIModel.MissingDocument;
}

const BankInformationRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className={cssExports.bankInformationRow}>
    <Typography className={cssExports.bankInformationLabel}>{label}</Typography>
    <Typography className={cssExports.bankInformationValue}>{value}</Typography>
  </div>
);

const BankInformationPanel: React.FC<BankInformationPanelProps> = ({ missingDocument }) => {
  const t = (key: string) => LocalizationHelperInstance.getFormattedMessage(key);

  const instructedAmount =
    missingDocument.instructedAmount != null
      ? LocalizationHelperInstance.getFormattedCurrency(
          missingDocument.instructedAmount,
          missingDocument.instructedCurrency,
          2,
          2
        )
      : undefined;

  const currencyExchange =
    missingDocument.currencyExchange != null && missingDocument.currencyExchange !== 0
      ? LocalizationHelperInstance.getFormattedNumber(missingDocument.currencyExchange, 2, 6)
      : undefined;

  // Field order is intentional: most relevant information first (per UX).
  const fields: { label: string; value?: string }[] = [
    { label: t(TranslationKeys.MissingDocument.Reference), value: missingDocument.entryReference },
    { label: t(TranslationKeys.MissingDocument.CounterpartyName), value: missingDocument.counterpartyName },
    { label: t(TranslationKeys.MissingDocument.CounterpartyAccount), value: missingDocument.counterpartyAccount },
    {
      label: t(TranslationKeys.MissingDocument.CounterpartyAccountType),
      value: missingDocument.counterpartyAccountType,
    },
    { label: t(TranslationKeys.MissingDocument.CounterpartyOrgNumber), value: missingDocument.counterpartyOrgNumber },
    { label: t(TranslationKeys.MissingDocument.AmountInCurrency), value: instructedAmount },
    { label: t(TranslationKeys.MissingDocument.Currency), value: missingDocument.instructedCurrency },
    { label: t(TranslationKeys.MissingDocument.ExchangeRate), value: currencyExchange },
    { label: t(TranslationKeys.MissingDocument.EndToEndId), value: missingDocument.endToEndId },
  ];

  const rows = fields.filter((field): field is { label: string; value: string } => !!field.value);

  if (rows.length === 0) return null;

  return (
    <div className={cssExports.bankInformationPanel}>
      <Accordion className={cssExports.bankInformationAccordion} disableGutters elevation={0} square>
        <AccordionSummary className={cssExports.bankInformationSummary} expandIcon={<ExpandMoreIcon />}>
          <Typography className={cssExports.bankInformationHeader}>
            {t(TranslationKeys.MissingDocument.BankInformation)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={cssExports.bankInformationDetails}>
          {rows.map((field) => (
            <BankInformationRow key={field.label} label={field.label} value={field.value} />
          ))}
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default BankInformationPanel;
